import {
  Box,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Input,
  Text,
} from '@chakra-ui/react'
import { useCallback, useEffect, useState } from 'react'
import PhoneCountrySelect from '../../../../../components/PhoneCountrySelect'
import { validatePhoneNumber } from '../../../../../utils/validatePhone'
import { countryNameToIso } from '../../../../../utils/countryCodes'

interface Props {
  phoneNumber: string
  phoneCountryCode?: string
  addressCountry?: string
  isRequired?: boolean
  label?: string
  onUpdate: (changes: {
    phoneNumber?: string
    phoneCountryCode?: string
  }) => void
}

export default function PhoneNumberInput({
  phoneNumber,
  phoneCountryCode,
  addressCountry,
  isRequired = false,
  label = 'Phone Number',
  onUpdate,
}: Props) {
  // Determine initial country code
  const getInitialCountryCode = useCallback(() => {
    if (phoneCountryCode) return phoneCountryCode
    if (addressCountry) {
      const isoCode = countryNameToIso(addressCountry)
      if (isoCode) return isoCode
    }
    return ''
  }, [phoneCountryCode, addressCountry])

  const [localPhone, setLocalPhone] = useState(phoneNumber || '')
  const [localCountryCode, setLocalCountryCode] = useState('')
  const [validationError, setValidationError] = useState('')
  const [isTouched, setIsTouched] = useState(false)

  // Update local state if props change (e.g., after loading user data)
  useEffect(() => {
    setLocalPhone(phoneNumber || '')
  }, [phoneNumber])

  // Initialize country code and propagate to parent
  useEffect(() => {
    const initialCode = getInitialCountryCode()
    if (initialCode && !localCountryCode) {
      setLocalCountryCode(initialCode)
      // Propagate the default country code to parent
      onUpdate({ phoneCountryCode: initialCode })
    }
  }, [getInitialCountryCode, localCountryCode, onUpdate])

  // Validate phone number
  const validate = useCallback(
    (phone: string, countryCode: string) => {
      if (!phone && !isTouched) {
        setValidationError('')
        return true
      }

      if (!phone) {
        setValidationError('')
        return true
      }

      if (!countryCode) {
        setValidationError('Please select a country code')
        return false
      }

      const result = validatePhoneNumber(phone, countryCode)
      setValidationError(result.errorMessage || '')
      return result.isValid
    },
    [isTouched]
  )

  function handlePhoneChange(value: string) {
    setLocalPhone(value)
    onUpdate({ phoneNumber: value })
  }

  function handleCountryCodeChange(value: string) {
    setLocalCountryCode(value)
    onUpdate({ phoneCountryCode: value })

    // Re-validate if already touched
    if (isTouched && localPhone) {
      validate(localPhone, value)
    }
  }

  function handleBlur() {
    setIsTouched(true)
    validate(localPhone, localCountryCode)
  }

  const hasError = isTouched && !!validationError

  return (
    <FormControl isInvalid={hasError}>
      <FormLabel>
        {label}{' '}
        {isRequired && (
          <Text as='span' color='red.500'>
            *
          </Text>
        )}
      </FormLabel>
      <HStack spacing={2} align='flex-start'>
        <Box flex='0 0 auto' minW='200px'>
          <PhoneCountrySelect
            value={localCountryCode}
            onChange={handleCountryCodeChange}
            hideLabel
          />
        </Box>
        <Box flex='1'>
          <Input
            type='tel'
            autoComplete='tel-national'
            placeholder='Enter phone number'
            value={localPhone}
            onChange={(e) => handlePhoneChange(e.target.value)}
            onBlur={handleBlur}
            aria-label='Phone number'
            aria-describedby={hasError ? 'phone-error' : undefined}
          />
        </Box>
      </HStack>
      {hasError && (
        <FormErrorMessage id='phone-error'>{validationError}</FormErrorMessage>
      )}
    </FormControl>
  )
}
